/**
 * usePatientDocument(uuid)
 *
 * Fetches a patient row and its associated document for a given visit UUID.
 *
 * Discovered API contract (from PatientPage.js):
 *   Patient:  supabase.from('notes-c').select('*').eq('id', uuid).single()
 *             (UUID-shaped params are treated as primary-key lookups;
 *              non-UUID strings fall back to the EHR_ID column)
 *
 *   Document: supabase.storage.from('files').download(path)
 *             path = New/MedADocs/[schema/]year/date/PatientName-PatientId.json
 *             where PatientId = patient.EHR_ID ?? patient.id
 *
 * Returns: { patient, document, isLoading, error, refetch }
 *
 *   patient  – the raw row from the notes-c table (or null)
 *   document – parsed JSON from storage (shape: { delta, sections, lastUpdated })
 *              or null if not yet fetched / not found
 *   isLoading – true while either fetch is in-flight
 *   error    – Error object if patient fetch failed; null otherwise
 *   refetch  – call to re-run both fetches
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { MEDSTUDENT_LIVE_COLLAB } from '../../config/collab';
import { templates } from '../../assets/templates';
import { loadCustomTemplate } from './customTemplates';
import { canReadOwnPatientRow, isPatientRole } from '../../config/roles';
import { COMBINED_UUID, buildCombinedPatientRow } from '../../documentComponents/medStudentCombine';
import { registerRosterPatientId } from '../../pages/medstudent/chartSource';
import { fetchPriorVisitCandidates } from '../../utils/priorVisitMatch';
import {
  isCutoverEffective, startChartRosterFromRds, serveChartRosterFromRds,
  startChartVisitFromRds, serveChartVisitFromRds,
} from '../../utils/rdsCutover';
import { rosterChartFields } from '../../utils/rosterPatientFields';

// Substitute placeholder tokens in a delta template using the patient row.
function fillTemplatePlaceholders(rawOps, patientRow) {
  const calcAge = (dob, visitDateStr) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const visit = visitDateStr ? new Date(visitDateStr) : new Date();
    let age = visit.getFullYear() - birth.getFullYear();
    const m = visit.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && visit.getDate() < birth.getDate())) age--;
    return String(age);
  };
  const fmtDate = (d) => {
    if (!d) return '';
    const [y, mo, dy] = d.split('-');
    return `${mo}/${dy}/${y}`;
  };
  const mrn = patientRow.EHR_ID || patientRow.id || '';
  const sex = (patientRow.sex || patientRow.gender || '').toString().toLowerCase();
  const gender = sex === 'm' || sex === 'male' ? 'male'
               : sex === 'f' || sex === 'female' ? 'female'
               : sex || '___';
  // Scheduled time without seconds; empty if missing.
  const time = (patientRow.time_ || '').toString().slice(0, 5);
  const subs = {
    '[time]': time,
    '[Patient Name]': patientRow.patient_name || '',
    '[MRN]': mrn,
    '[age]': calcAge(patientRow.dob, patientRow.date_),
    '[gender]': gender,
    '[MM/DD/YYYY]': fmtDate(patientRow.date_),
  };
  return rawOps.map(op => {
    if (typeof op.insert !== 'string') return op;
    let text = op.insert;
    for (const [ph, val] of Object.entries(subs)) text = text.split(ph).join(val);
    return { ...op, insert: text };
  });
}

// Build an empty document filled with the right template based on the patient's `type`.
// For medstudent-facing types we prefer the MEDSTUDENT_<TYPE> variant (e.g. the
// design.html-inspired NP template) when one exists; otherwise we fall through
// to the doctor's standard template, then finally to NP as a last resort.
/**
 * Which template code this row should use.
 *
 *   row.template (trimmed, non-empty)  →  row.type  →  'NP'
 *
 * Mirrored by `effective_template_code` in backend/api/collab/seed.py — the
 * collab server seeds new rooms server-side, so if only one side knew this
 * rule a fresh document would be seeded from a different template than the
 * one Reload Template offers.
 *
 * The result also selects the seeding MODE, not just the file: an effective
 * code of FFP/FFS means "copy the most recent populated prior visit".
 */
// Pin marking a code as "the BUILT-IN template of this name, not the custom one
// that shares it". Duplicated here rather than imported from
// pages/medstudent/templateCatalog so this hook keeps no dependency on the page
// layer; templateCatalog.BUILTIN_PREFIX is the same literal and its tests pin it.
const BUILTIN_PIN = 'BUILTIN:';

export function isBuiltinPinnedCode(code) {
  return String(code || '').trim().toUpperCase().startsWith(BUILTIN_PIN);
}

export function stripBuiltinPin(code) {
  const c = String(code || '').trim().toUpperCase();
  return c.startsWith(BUILTIN_PIN) ? c.slice(BUILTIN_PIN.length) : c;
}

export function effectiveTemplateCode(patientRow) {
  const explicit = String(patientRow?.template || '').trim();
  if (explicit) return explicit.toUpperCase();
  const type = String(patientRow?.type || '').trim();
  return type ? type.toUpperCase() : 'NP';
}

export function buildInitialDocFromTemplate(patientRow) {
  // A BUILTIN: pin only says "skip the custom lookup" — the template key
  // itself is the bare code.
  const rawType = stripBuiltinPin(effectiveTemplateCode(patientRow));
  const baseKey = templates[rawType] ? rawType : 'NP';
  const medKey = `MEDSTUDENT_${baseKey}`;
  const key = templates[medKey] ? medKey : baseKey;
  const tpl = templates[key] || templates.NP;
  // Templates hold their ops under `delta.ops` (NP/NS) OR, for some
  // (ST/PR/SO), under `content.ops` — the same shape EditableDocumentPage
  // tolerates via `template.delta || template.content`. The string fallback
  // is guarded by a typeof check: an OBJECT `content` (e.g. `{ ops: [...] }`)
  // would otherwise coerce to the literal "[object Object]" — the bug that
  // made "Reload template" populate [object Object] for ST/PR/SO visits.
  const rawOps =
    (tpl?.delta?.ops?.length > 0 && tpl.delta.ops) ||
    (Array.isArray(tpl?.content?.ops) && tpl.content.ops.length > 0 && tpl.content.ops) ||
    (typeof tpl?.content === 'string' && tpl.content ? [{ insert: `${tpl.content}\n` }] : [{ insert: '\n' }]);
  console.log('[usePatientDocument] seeding from template', { rawType, key, opsCount: rawOps.length });
  return {
    delta: { ops: fillTemplatePlaceholders(rawOps, patientRow) },
    sections: [],
    lastUpdated: new Date().toISOString(),
    _initializedFromTemplate: key,
  };
}

// Resolve the document a fresh visit should seed from. Prefers the current
// user's saved CUSTOM template (keyed by the row's `type` code) so a med
// student can reuse their own templates by typing the code into the
// AddPatientModal Type field; falls back to the built-in type-matched template
// when no custom template exists (or the lookup fails). Async because the
// custom template lives in storage. Shared by seeding and "Reload template".
export async function resolveTemplateDoc(patientRow) {
  const code = effectiveTemplateCode(patientRow);
  // A BUILTIN: pin means the user explicitly chose the system template over a
  // custom one sharing the same acronym — skip the storage lookup entirely,
  // otherwise the custom would win as it always has.
  if (code && !isBuiltinPinnedCode(code)) {
    try {
      const custom = await loadCustomTemplate(code);
      const ops = custom?.delta?.ops;
      if (Array.isArray(ops) && ops.length > 0) {
        return {
          delta: { ops: fillTemplatePlaceholders(ops, patientRow) },
          sections: Array.isArray(custom.sections) ? custom.sections : [],
          lastUpdated: new Date().toISOString(),
          _initializedFromCustomTemplate: code,
        };
      }
    } catch (_) {
      // Storage hiccup — fall through to the built-in template.
    }
  }
  return buildInitialDocFromTemplate(patientRow);
}

// Follow-up visit types — FFP (follow-up patient) and FFS (follow-up sick) —
// should NOT start from a blank template; they're continuations of an
// existing patient's care. When a medstudent first opens an FFP/FFS visit,
// seed the document by copying the most recent PRIOR visit's content for the
// same patient — "same patient" per utils/priorVisitMatch, the rule the
// Copy-from-older search uses too. The student can then edit / append to that
// record. Falls back to the type-matched template if no prior visit is found
// or its doc fails to download.
export const FOLLOW_UP_TYPES = new Set(['FFP', 'FFS']);

async function buildInitialDocFromLastVisit(patientRow, supabase, schema) {
  const patientName = patientRow?.patient_name;
  const currentDate = patientRow?.date_;
  if (!patientName || !currentDate) return null;

  // Same-patient rule and query as "Copy from older session" — name must
  // match, and a real EHR ID on both rows must match too. This used to search
  // by name alone (and against the DEFAULT schema, never the tenant one), so a
  // second patient with the same name carried the wrong chart forward.
  const { candidates: priorRows, error } = await fetchPriorVisitCandidates(
    supabase, patientRow, schema,
    { log: (info) => console.log('[usePatientDocument] follow-up prior-visit query', info) },
  );
  if (error || priorRows.length === 0) {
    console.log('[usePatientDocument] no prior visits for follow-up', {
      patientName, currentDate, schema, error: error?.message,
    });
    return null;
  }

  for (const prior of priorRows) {
    const path = buildDocPath(prior, schema);
    if (!path) continue;
    try {
      const { data: blob, error: dlErr } = await supabase.storage.from('files').download(path);
      if (dlErr || !blob) continue;
      const text = await blob.text();
      const parsed = JSON.parse(text);
      const ops = parsed?.delta?.ops;
      const isEmpty =
        !ops ||
        (Array.isArray(ops) && ops.length === 0) ||
        (Array.isArray(ops) && ops.length === 1 && (!ops[0]?.insert || ops[0].insert === '\n'));
      if (isEmpty) continue;
      // Re-run template-token substitution against the CURRENT visit's row so
      // any unfilled [time]/[MM/DD/YYYY] placeholders pick up today's values
      // rather than the prior visit's. Idempotent when there are no tokens.
      const refilledOps = fillTemplatePlaceholders(ops, patientRow);
      console.log('[usePatientDocument] seeding follow-up from prior visit', {
        priorDate: prior.date_,
        priorType: prior.type,
        opsCount: refilledOps.length,
      });
      return {
        delta: { ops: refilledOps },
        sections: Array.isArray(parsed.sections) ? parsed.sections : [],
        lastUpdated: new Date().toISOString(),
        _initializedFromLastVisit: prior.date_,
      };
    } catch (e) {
      console.warn('[usePatientDocument] prior-visit fetch failed:', e?.message);
    }
  }
  return null;
}

// Returns the seeded document. FFP/FFS visit types silently copy the most
// recent prior visit (continuation of care — student wants to see what was
// already documented). All other types start from the type-matched template.
//
// `notes_behavior === 'accumulate'` is handled by the clinic PAGE, not here:
// MedStudentClinicPage fires the same prompt the doctor flow uses
// ("Found older doc on <date>, copy over?") via the Copy-Older modal once
// the doc has loaded, so the user gets to choose. Silent-copy for accumulate
// would surprise the medstudent (they could lose their template-formatted
// blank doc to a previous visit's content).
async function buildInitialDoc(patientRow, supabase, schema) {
  // The EFFECTIVE code, not the raw type — a row whose `template` says FFP
  // carries over even when its `type` is NP, and vice versa.
  const code = effectiveTemplateCode(patientRow);
  const behavior = String(patientRow?.notes_behavior || '').toLowerCase().trim();
  // 'fresh' explicitly opts OUT of accumulation, even for FFP/FFS.
  const shouldAccumulate = behavior !== 'fresh' && FOLLOW_UP_TYPES.has(code);
  if (shouldAccumulate) {
    const fromPrior = await buildInitialDocFromLastVisit(patientRow, supabase, schema);
    if (fromPrior) return fromPrior;
  }
  // Custom (user-scoped) template by type code, else the built-in template.
  return resolveTemplateDoc(patientRow);
}

// UUID v1-v5 pattern — matches what PatientPage.isScheduleRowUuidParam uses
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuidParam(param) {
  return typeof param === 'string' && UUID_RE.test(param.trim());
}

// Does this patient row actually belong to the /clinic/:uuid param?
//
// The hook no longer carries the PREVIOUS visit's row across a switch (that is
// what left the header describing the wrong patient), but a row can still be
// paired with a uuid it does not belong to: a `seed` handed over from nav
// state, or a row captured in a closure before the switch. Anything
// destructive (template re-seed, ledger writes) must verify the pairing with
// this before acting. Mirrors how the row was
// fetched: by `id` for uuid params (visit, roster, combined sentinel), by
// `EHR_ID` for deep-link params.
export function rowMatchesVisitParam(row, param) {
  if (!row || param == null || param === '') return false;
  const p = String(param).trim();
  if (row.id != null && String(row.id) === p) return true;
  if (row.EHR_ID != null && String(row.EHR_ID) === p) return true;
  return false;
}

/**
 * Build the MedADocs storage path.
 * Mirrors generateMedADocsPath() from documentUtilities.js but inlined to
 * avoid pulling in that module's heavy transitive deps during tests.
 *
 * Path format: New/MedADocs/[schema/]year/date/filename
 * (schema segment is omitted when schema === 'public')
 */
export function buildDocPath(patientRow, schema) {
  if (!patientRow) return null;
  // Patient-level roster charts have no visit date — their doc lives under a
  // dateless Patients/ segment keyed by the roster row id.
  if (patientRow._source === 'roster') {
    const name = patientRow.patient_name || 'Unknown';
    const schemaPrefix = schema && schema !== 'public' ? `${schema}/` : '';
    return `New/MedADocs/${schemaPrefix}Patients/${name}-${patientRow.id}.json`;
  }
  const { patient_name, EHR_ID, id, date_ } = patientRow;
  if (!date_) return null;

  const patientName = patient_name || 'Unknown';
  const patientId = EHR_ID || id;
  const fileName = `${patientName}-${patientId}.json`;

  const year = date_.split('-')[0];
  const schemaPrefix = schema && schema !== 'public' ? `${schema}/` : '';
  return `New/MedADocs/${schemaPrefix}${year}/${date_}/${fileName}`;
}

// Map a patients_with_ehrid row to the visit-row shape the clinic page expects.
// Extra roster columns (user-added import columns etc.) spread through.
//
// The ehr_id/phone_numbers translation itself lives in rosterPatientFields so
// the quick-add suggestion list applies the same one — it used to spread raw
// roster rows into a visit-shaped form, which is why EHR ID and phone came up
// blank for a patient who had never been scheduled.
export function mapRosterRowToPatient(row, schema) {
  if (!row) return null;
  registerRosterPatientId(row.id);
  return {
    ...row,
    ...rosterChartFields(row),
    _source: 'roster',
    ...(schema ? { _schema: schema } : {}),
  };
}

export function usePatientDocument(uuid, options = {}) {
  const {
    readOnly = false, viewer = null, combinedDate = null, source = 'visit',
    schema: schemaOpt = null,
    // The row the caller ALREADY has for this uuid (the sidebar card that was
    // just clicked, passed through nav state). Used only to fill the gap
    // between switching charts and the fetch landing — see below.
    seed = null,
  } = options;
  const [patient, setPatient] = useState(null);
  const [document, setDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tracks the latest fetch's "generation" so an in-flight fetch for a
  // previous uuid can't overwrite state belonging to the current uuid.
  const fetchGenRef = useRef(0);
  // Which uuid the CURRENTLY HELD patient/document belong to. Everything the
  // page derives from `patient` — the schema it reads and writes, the document
  // storage path, the row's creator — is only meaningful next to the chart it
  // came from.
  const loadedUuidRef = useRef(null);

  const fetchAll = useCallback(async () => {
    const myGen = ++fetchGenRef.current;
    // Keep the current data across a REFETCH of the same chart: clearing there
    // made PatientHeaderBar (which renders null without a `patient`) vanish and
    // come back, the "flicker their page, then mine, then theirs" this hook was
    // fixed for once already.
    //
    // But NEVER keep it across a SWITCH. Holding the previous patient meant the
    // header kept describing them — their schema chip, their creator, their
    // document path — while the new chart loaded underneath, and on a fetch
    // error it never corrected itself at all. A chart labelled with someone
    // else's schema and owner is worse than one that is briefly bare, and for a
    // cross-schema chart it names the wrong clinic outright.
    //
    // `seed` closes the gap the old behaviour was really covering: the sidebar
    // hands over the row it just rendered, so the header switches to the RIGHT
    // patient immediately instead of blanking.
    if (loadedUuidRef.current !== uuid) {
      // Same pairing check every other consumer uses — nav state outlives the
      // navigation that set it, so a stale seed must never be adopted.
      const usableSeed = rowMatchesVisitParam(seed, uuid) ? seed : null;
      setPatient(usableSeed);
      setDocument(null);
      loadedUuidRef.current = usableSeed ? uuid : null;
    }
    setIsLoading(true);
    setError(null);

    const isCurrent = () => fetchGenRef.current === myGen;
    // Data that does not belong to the requested chart must not stay on screen.
    const dropStaleChart = () => {
      setPatient(null);
      setDocument(null);
      loadedUuidRef.current = null;
    };
    // Commit, and record WHICH chart the state now describes. Recording it is
    // what lets a refetch of the same chart keep the header up (no flicker)
    // while a switch still clears it.
    const commitChart = (row, doc) => {
      setPatient(row);
      setDocument(doc);
      loadedUuidRef.current = uuid;
    };

    if (!uuid) {
      if (!isCurrent()) return;
      dropStaleChart();
      setIsLoading(false);
      return;
    }

    // ── Combined day-view ───────────────────────────────────────────────
    // A synthetic med-student document (the "Combine Documents" button), NOT
    // a real notes-c row. Skip the DB lookup and build the matching synthetic
    // patient row so the collab room path resolves to `<date>-combined.json`
    // — the path the Combine button wrote. With live collab ON the doc is
    // owned by the collab room (the server hydrates it from that .json on
    // connect), so we leave `document` null exactly like the normal collab
    // path; with collab OFF we load the combined .json directly.
    // See documentComponents/medStudentCombine.js.
    if (uuid === COMBINED_UUID) {
      const date = combinedDate;
      if (!date) {
        if (!isCurrent()) return;
        setError(new Error('Combined view requires a ?date= parameter'));
        dropStaleChart();
        setIsLoading(false);
        return;
      }
      const patientRow = buildCombinedPatientRow(date);
      let nextDocument = null;
      if (!MEDSTUDENT_LIVE_COLLAB) {
        try {
          const supabase = await getSupabase();
          if (!isCurrent()) return;
          const schema = supabaseSchema || 'public';
          const docPath = buildDocPath(patientRow, schema);
          const { data: blob } = await supabase.storage.from('files').download(docPath);
          if (!isCurrent()) return;
          if (blob) nextDocument = JSON.parse(await blob.text());
        } catch (e) {
          console.warn('[usePatientDocument] combined doc load failed:', e?.message);
        }
      }
      if (!isCurrent()) return;
      setError(null);
      commitChart(patientRow, nextDocument);
      setIsLoading(false);
      return;
    }

    try {
      const supabase = await getSupabase();
      if (!isCurrent()) return;

      // Which Postgres schema this fetch runs against. `schemaOpt` (an
      // explicit `schema` option, e.g. a linked provider's schema from a
      // cross-schema chart) wins; otherwise fall back to the viewer's own
      // active schema, exactly like every pre-existing call site. Scoping via
      // `.schema(effSchema)` even in the default case is a no-op against the
      // real client — `.schema('public').from(t)` returns the same rows as
      // the plain client — so default (no-`schema`-option) behavior is
      // unchanged.
      const effSchema = schemaOpt || supabaseSchema || 'public';

      // Phase 4 cutover, site 9 (utils/rdsCutover.js `chart_roster` /
      // `chart_visit`): when both switches are on, RDS is asked for the same
      // one row alongside the Supabase read; Supabase's row stays the parity
      // oracle and the fallback, and a Supabase error still ends the fetch
      // exactly as before (no oracle, RDS not consulted). Off = byte-for-byte
      // the old behaviour.
      const chartCutover = await isCutoverEffective(source === 'roster' ? 'chart_roster' : 'chart_visit');
      if (!isCurrent()) return;

      // ── 1. Fetch patient row ────────────────────────────────────────────
      let patientRow;
      if (source === 'roster') {
        const rdsStarted = chartCutover ? startChartRosterFromRds(effSchema, { id: uuid }) : null;
        const { data: rosterRow, error: rosterError } = await supabase
          .schema(effSchema)
          .from('patients_with_ehrid')
          .select('*')
          .eq('id', uuid)
          .single();
        if (!isCurrent()) return;
        if (rosterError) {
          setError(rosterError);
          // The requested chart could not be read, so nothing about it is
          // known. Leaving the previous patient here is what made the stale
          // header PERMANENT rather than momentary.
          dropStaleChart();
          setIsLoading(false);
          return;
        }
        let rosterRowUsed = rosterRow;
        if (rdsStarted) {
          const served = await serveChartRosterFromRds(effSchema, {
            id: uuid, supabaseRows: rosterRow ? [rosterRow] : [], started: rdsStarted,
          });
          if (!isCurrent()) return;
          // Exactly one row is the answer; anything else keeps Supabase's.
          if (served.rows.length === 1) rosterRowUsed = served.rows[0];
        }
        patientRow = mapRosterRowToPatient(rosterRowUsed, effSchema);
      } else {
        const lookupColumn = isUuidParam(uuid) ? 'id' : 'EHR_ID';
        const rdsStarted = chartCutover
          ? startChartVisitFromRds(effSchema, { column: lookupColumn, value: uuid })
          : null;
        const { data: visitRow, error: patientError } = await supabase
          .schema(effSchema)
          .from('notes-c')
          .select('*')
          .eq(lookupColumn, uuid)
          .single();
        if (!isCurrent()) return;
        if (patientError) {
          setError(patientError);
          // See the roster branch: a failed read must not leave the previous
          // patient's schema, owner and document path on screen.
          dropStaleChart();
          setIsLoading(false);
          return;
        }
        let visitRowUsed = visitRow;
        if (rdsStarted) {
          const served = await serveChartVisitFromRds(effSchema, {
            column: lookupColumn, value: uuid,
            supabaseRows: visitRow ? [visitRow] : [], started: rdsStarted,
          });
          if (!isCurrent()) return;
          // Two rows is the ambiguity .single() refuses: keep Supabase's.
          if (served.rows.length === 1) visitRowUsed = served.rows[0];
        }
        patientRow = { ...visitRowUsed, _schema: effSchema };
      }

      if (viewer && isPatientRole(viewer.role) && !canReadOwnPatientRow(viewer, patientRow)) {
        setError(new Error('Patient access denied'));
        dropStaleChart();
        setIsLoading(false);
        return;
      }

      // ── 2. Build document path & fetch from storage ─────────────────────
      // When live collab is enabled, the document state is owned by the
      // collab WebSocket — the server hydrates from .yjs / .json / seed
      // and broadcasts the current state on connect. Skipping the fetch
      // here avoids racing the WS hydration and double-seeding fresh docs.
      const schema = effSchema;
      const docPath = buildDocPath(patientRow, schema);
      let nextDocument = null;

      if (MEDSTUDENT_LIVE_COLLAB) {
        // Collab (incl. roster charts now) — the WS owns the document and the
        // room hydrates from this `.json` path server-side. Skip the client
        // fetch to avoid racing the WS hydration / double-seeding.
        console.log('[clinic doc] collab flag ON — skipping client-side doc fetch', { docPath });
      } else if (!docPath) {
        console.log('[clinic doc] no docPath — patient row missing date_', { uuid, patient_name: patientRow?.patient_name });
      } else {
        console.log('[clinic doc] loading from', docPath);
        const { data: blob, error: storageError } = await supabase.storage
          .from('files')
          .download(docPath);
        if (!isCurrent()) return;

        if (storageError) {
          // Storage download failed. The most common reason is the document
          // doesn't exist yet for a freshly-added patient — Supabase's error
          // shape varies. Seed whenever the download fails AND we have a
          // patient row. For FFP/FFS visits the seed copies the most recent
          // prior visit's doc; otherwise it uses the type-matched template.
          // upsert:false ensures a real race where the doc DOES exist doesn't
          // get clobbered.
          console.warn('[usePatientDocument] storage download failed:', {
            message: storageError?.message,
            statusCode: storageError?.statusCode,
            name: storageError?.name,
            docPath,
          });
          if (readOnly) {
            nextDocument = null;
          } else {
            const seeded = await buildInitialDoc(patientRow, supabase, schema);
            if (!isCurrent()) return;
            try {
              const seedBlob = new Blob([JSON.stringify(seeded)], { type: 'application/json' });
              const { error: uploadErr } = await supabase.storage
                .from('files')
                .upload(docPath, seedBlob, { upsert: false });
              if (uploadErr && !/exists/i.test(uploadErr.message || '')) {
                console.warn('[usePatientDocument] template upload failed:', uploadErr);
              }
            } catch (e) {
              console.warn('[usePatientDocument] template upload threw:', e?.message);
            }
            if (!isCurrent()) return;
            nextDocument = seeded;
          }
        } else {
          try {
            const text = await blob.text();
            if (!isCurrent()) return;
            const parsed = JSON.parse(text);
            const ops = parsed?.delta?.ops;
            const isEffectivelyEmpty = !ops || (Array.isArray(ops) && ops.length === 0) ||
              (Array.isArray(ops) && ops.length === 1 && (!ops[0]?.insert || ops[0].insert === '\n'));
            if (isEffectivelyEmpty) {
              // Same FFP/FFS-aware seed path as the missing-doc case — an
              // empty-but-existing file is functionally the same as no file.
              if (readOnly) {
                nextDocument = null;
              } else {
                console.log('[usePatientDocument] existing doc is empty — seeding');
                const seeded = await buildInitialDoc(patientRow, supabase, schema);
                if (!isCurrent()) return;
                try {
                  const blob2 = new Blob([JSON.stringify(seeded)], { type: 'application/json' });
                  await supabase.storage.from('files').upload(docPath, blob2, { upsert: true });
                } catch (_) { /* fine */ }
                if (!isCurrent()) return;
                nextDocument = seeded;
              }
            } else {
              // Back-fill template substitutions if the saved doc still has tokens.
              const hasToken = (s) => typeof s === 'string' && /\[(time|Patient Name|MRN|age|gender|MM\/DD\/YYYY)\]/.test(s);
              const needsRefill = Array.isArray(ops) && ops.some(op => hasToken(op.insert));
              if (needsRefill && !readOnly) {
                console.log('[usePatientDocument] existing doc still has template tokens — re-substituting');
                const refilled = { ...parsed, delta: { ops: fillTemplatePlaceholders(ops, patientRow) } };
                try {
                  const blob2 = new Blob([JSON.stringify(refilled)], { type: 'application/json' });
                  await supabase.storage.from('files').upload(docPath, blob2, { upsert: true });
                } catch (_) { /* fine */ }
                if (!isCurrent()) return;
                nextDocument = refilled;
              } else {
                nextDocument = parsed;
              }
            }
          } catch (e) {
            console.warn('[usePatientDocument] doc parse failed, seeding from template:', e?.message);
            nextDocument = readOnly ? null : buildInitialDocFromTemplate(patientRow);
          }
        }
      }

      // ── 3. Commit atomically ────────────────────────────────────────────
      // React 18 batches these two setStates in the same microtask, so
      // consumers see (patient, document) flip together — no intermediate
      // (B_patient, A_document) frame.
      if (!isCurrent()) return;
      commitChart(patientRow, nextDocument);
      setIsLoading(false);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err);
      setIsLoading(false);
    }
    // `seed` is deliberately NOT a dependency: it is only read on a uuid
    // change, and nav state produces a new object identity on every render —
    // depending on it would refetch the chart endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, readOnly, viewer, combinedDate, source, schemaOpt]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { patient, document, isLoading, error, refetch: fetchAll };
}
